import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, ConfigProvider, Input, Modal, QRCode, Spin, message, theme as antdTheme } from 'antd';
import {
  ApiOutlined,
  CheckCircleFilled,
  CheckOutlined,
  CopyOutlined,
  CrownFilled,
  DisconnectOutlined,
  ExclamationCircleFilled,
  LinkOutlined,
  LoadingOutlined,
  ReloadOutlined,
  RobotOutlined,
  StarFilled,
  ThunderboltFilled,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import { useProMessages } from '../i18n/pro';
import {
  apiErrorMessage,
  BotHealth,
  BotView,
  PlanOrder,
  PlanOverview,
  plansApi,
  PlanPaymentMethod,
} from '../api/plans';
import { openStarsInvoice } from '../telegram/webApp';
import './Pro.css';

const fill = (template: string, values: Record<string, string | number>) =>
  Object.entries(values).reduce((out, [key, value]) => out.split(`{${key}}`).join(String(value)), template);

const formatTon = (value: number) => value.toLocaleString(undefined, { maximumFractionDigits: 4 });
const nanoToTon = (nano: string) => Number(nano) / 1e9;
const SUPPORT_HANDLE = '@trictoune';
const SUPPORT_URL = 'https://t.me/trictoune';
const POLL_INTERVAL_MS = 4000;

const CopyField: React.FC<{ label: string; value: string; hint?: string; copiedLabel: string }> = ({
  label,
  value,
  hint,
  copiedLabel,
}) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      message.error('Copy failed');
    }
  };
  return (
    <div className="pro-copy">
      <span className="pro-copy-label">{label}</span>
      <div className="pro-copy-row">
        <code>{value}</code>
        <button type="button" className="pro-copy-btn" onClick={copy} aria-label={label}>
          {copied ? <CheckOutlined /> : <CopyOutlined />}
          {copied && <span>{copiedLabel}</span>}
        </button>
      </div>
      {hint && <span className="pro-copy-hint">{hint}</span>}
    </div>
  );
};

/** Thème antd clair : l'app est blanche alors que le thème global est sombre. */
const LIGHT_THEME = {
  algorithm: antdTheme.defaultAlgorithm,
  token: {
    colorPrimary: '#f6851b',
    colorInfo: '#f6851b',
    borderRadius: 10,
    colorText: '#1a1a1a',
    colorTextSecondary: 'rgba(26, 26, 26, 0.6)',
    colorBgContainer: '#ffffff',
    colorBgElevated: '#ffffff',
    colorBorder: '#e3e2e0',
    colorBorderSecondary: '#ececea',
  },
  components: {
    Input: { colorBgContainer: '#ffffff', colorBorder: '#e3e2e0', colorText: '#1a1a1a', colorTextPlaceholder: '#9b9a97' },
  },
};

const Pro: React.FC = () => (
  <ConfigProvider theme={LIGHT_THEME}>
    <ProInner />
  </ConfigProvider>
);

const ProInner: React.FC = () => {
  const { locale } = useI18n();
  const p = useProMessages().app;
  const { refreshUser } = useAuth();

  const [overview, setOverview] = useState<PlanOverview | null>(null);
  const [health, setHealth] = useState<BotHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [order, setOrder] = useState<PlanOrder | null>(null);
  const [starting, setStarting] = useState<PlanPaymentMethod | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [justPaid, setJustPaid] = useState(false);
  const [now, setNow] = useState(Date.now());

  const [token, setToken] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [botBusy, setBotBusy] = useState(false);
  const [botError, setBotError] = useState<string | null>(null);

  const checkoutRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await plansApi.getMe();
      setOverview(data);
      setError(null);
      if (data.bot) {
        plansApi
          .getBot()
          .then((res) => setHealth(res.health))
          .catch(() => setHealth(null));
      } else {
        setHealth(null);
      }
    } catch (err) {
      setError(apiErrorMessage(err, p.loadError));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /* ---------- Attente du paiement : sondage de la commande ---------- */
  const pendingOrderId = order && order.status === 'PENDING' ? order.orderId : null;
  useEffect(() => {
    if (!pendingOrderId) return;
    let stopped = false;
    const tick = async () => {
      try {
        const fresh = await plansApi.getOrder(pendingOrderId);
        if (stopped) return;
        if (fresh.status === 'PAID') {
          setOrder(null);
          setJustPaid(true);
          await load();
          refreshUser().catch(() => undefined);
          return;
        }
        // Conserve le même objet tant que rien n'a changé (évite de redessiner le QR code).
        setOrder((prev) =>
          prev && prev.orderId === fresh.orderId && prev.ton?.underpaidNano === fresh.ton?.underpaidNano ? prev : fresh,
        );
      } catch {
        /* réseau instable : on réessaie au prochain passage */
      }
    };
    const timer = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [pendingOrderId, load, refreshUser]);

  /* ---------- Compte à rebours du cours GRAM (ex-TON) ---------- */
  const quoteEnd = order?.ton?.expiresAt ? new Date(order.ton.expiresAt).getTime() : null;
  useEffect(() => {
    if (!quoteEnd) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [quoteEnd]);

  const startCheckout = async (method: PlanPaymentMethod) => {
    setCheckoutError(null);
    setJustPaid(false);
    setStarting(method);
    try {
      const created = await plansApi.checkout(method);
      setOrder(created);
      setTimeout(() => checkoutRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
    } catch (err) {
      setCheckoutError(apiErrorMessage(err, p.checkoutError));
    } finally {
      setStarting(null);
    }
  };

  const connectBot = async (e: React.FormEvent) => {
    e.preventDefault();
    setBotError(null);
    setConnecting(true);
    try {
      const { bot } = await plansApi.connectBot(token.trim());
      setToken('');
      message.success(fill(p.botConnected, { bot: bot.username }));
      await load();
    } catch (err) {
      setBotError(apiErrorMessage(err, p.checkoutError));
    } finally {
      setConnecting(false);
    }
  };

  const repairBot = async () => {
    setBotBusy(true);
    setBotError(null);
    try {
      await plansApi.repairBot();
      message.success(p.repaired);
      await load();
    } catch (err) {
      setBotError(apiErrorMessage(err, p.checkoutError));
    } finally {
      setBotBusy(false);
    }
  };

  const disconnectBot = (bot: BotView) => {
    Modal.confirm({
      title: p.disconnect,
      content: fill(p.disconnectConfirm, { bot: bot.username }),
      okText: p.disconnect,
      okButtonProps: { danger: true },
      cancelText: p.cancel,
      onOk: async () => {
        try {
          await plansApi.disconnectBot();
          message.success(p.botDisconnected);
          await load();
        } catch (err) {
          setBotError(apiErrorMessage(err, p.checkoutError));
        }
      },
    });
  };

  const plan = overview?.plan;
  const pricing = overview?.pricing;
  const isActive = !!plan?.active;
  const expiredOn = !isActive && plan?.expiresAt ? new Date(plan.expiresAt) : null;
  const expiresAt = plan?.expiresAt ? new Date(plan.expiresAt) : null;
  const dateFmt = useMemo(() => new Intl.DateTimeFormat(locale, { dateStyle: 'long' }), [locale]);

  if (loading) {
    return (
      <div className="pro-loading">
        <Spin />
      </div>
    );
  }

  const quoteSecondsLeft = quoteEnd ? Math.max(0, Math.floor((quoteEnd - now) / 1000)) : null;
  const quoteExpired = quoteSecondsLeft === 0 && order?.method === 'TON';
  const formatCountdown = (seconds: number) =>
    `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

  const renderCheckout = () => {
    if (!order) return null;
    return (
      <div className="pro-card pro-checkout" ref={checkoutRef}>
        {order.method === 'TON' && order.ton && (
          <>
            <div className="pro-checkout-head">
              <h3>{p.tonTitle}</h3>
              <button type="button" className="pro-link-btn" onClick={() => setOrder(null)}>
                {p.cancel}
              </button>
            </div>
            <div className="pro-ton-grid">
              <div className="pro-ton-fields">
                <CopyField
                  label={p.tonAmountLabel}
                  value={`${formatTon(order.ton.amount)} GRAM`}
                  copiedLabel={p.copied}
                />
                <CopyField label={p.tonAddressLabel} value={order.ton.address} copiedLabel={p.copied} />
                <CopyField
                  label={p.tonMemoLabel}
                  value={order.ton.memo}
                  hint={p.tonMemoHint}
                  copiedLabel={p.copied}
                />
                <a className="pro-btn pro-btn--primary" href={order.ton.tonkeeperLink} target="_blank" rel="noopener noreferrer">
                  <ThunderboltFilled /> {p.openWallet}
                </a>
              </div>
              <div className="pro-qr">
                <QRCode value={order.ton.deepLink} size={168} bordered={false} color="#1a1a1a" bgColor="#ffffff" />
                <span>{p.scanQr}</span>
              </div>
            </div>
            {order.ton.underpaidNano && (
              <Alert
                type="warning"
                showIcon
                message={fill(p.underpaid, { amount: formatTon(nanoToTon(order.ton.underpaidNano)) })}
                description={
                  <a href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">
                    {p.supportLabel} {SUPPORT_HANDLE}
                  </a>
                }
              />
            )}
            <div className="pro-waiting">
              <LoadingOutlined />
              <span>{p.waiting}</span>
              {quoteSecondsLeft !== null && !quoteExpired && (
                <span className="pro-quote">{fill(p.quoteExpires, { time: formatCountdown(quoteSecondsLeft) })}</span>
              )}
            </div>
            {quoteExpired && (
              <div className="pro-quote-expired">
                <span>{p.quoteExpired}</span>
                <Button size="small" icon={<ReloadOutlined />} onClick={() => startCheckout('TON')} loading={starting === 'TON'}>
                  {p.refreshQuote}
                </Button>
              </div>
            )}
          </>
        )}

        {order.method === 'STARS' && order.stars && (
          <>
            <div className="pro-checkout-head">
              <h3>{p.starsTitle}</h3>
              <button type="button" className="pro-link-btn" onClick={() => setOrder(null)}>
                {p.cancel}
              </button>
            </div>
            <p className="pro-muted">{p.starsHint}</p>
            <button
              type="button"
              className="pro-btn pro-btn--primary pro-btn--lg"
              onClick={() => openStarsInvoice(order.stars!.invoiceLink)}
            >
              <StarFilled /> {fill(p.starsPayNow, { amount: order.stars.amount.toLocaleString() })}
            </button>
            <div className="pro-waiting">
              <LoadingOutlined />
              <span>{p.waiting}</span>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderPayButtons = () => (
    <div className="pro-pay">
      <h3>{p.payTitle}</h3>
      <div className="pro-pay-buttons">
        <button
          type="button"
          className="pro-pay-btn"
          disabled={!pricing?.ton.enabled || starting !== null}
          onClick={() => startCheckout('TON')}
        >
          <span className="pro-pay-icon pro-pay-icon--ton">
            <ThunderboltFilled />
          </span>
          <span className="pro-pay-text">
            <strong>{p.payTon}</strong>
            <small>
              {pricing?.ton.enabled
                ? pricing.ton.amount
                  ? fill(p.payTonSub, { amount: formatTon(pricing.ton.amount) })
                  : ''
                : p.tonUnavailable}
            </small>
          </span>
          {starting === 'TON' && <LoadingOutlined />}
        </button>
        <button type="button" className="pro-pay-btn" disabled={starting !== null} onClick={() => startCheckout('STARS')}>
          <span className="pro-pay-icon pro-pay-icon--stars">
            <StarFilled />
          </span>
          <span className="pro-pay-text">
            <strong>{p.payStars}</strong>
            <small>{pricing ? fill(p.payStarsSub, { amount: pricing.stars.amount.toLocaleString() }) : ''}</small>
          </span>
          {starting === 'STARS' && <LoadingOutlined />}
        </button>
      </div>
      {checkoutError && <Alert type="error" showIcon message={checkoutError} />}
    </div>
  );

  const bot = overview?.bot || null;

  return (
    <div className="pro-page">
      <div className="pro-header">
        <p className="pro-eyebrow">{p.eyebrow}</p>
        <h1>{p.title}</h1>
        <p className="pro-subtitle">{p.subtitle}</p>
      </div>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}

      {justPaid && (
        <Alert
          className="pro-paid"
          type="success"
          showIcon
          icon={<CheckCircleFilled />}
          message={p.paid}
          closable
          onClose={() => setJustPaid(false)}
        />
      )}

      {/* ------------------------- Statut + achat ------------------------- */}
      <div className={`pro-card pro-status ${isActive ? 'pro-status--active' : ''}`}>
        <div className="pro-status-main">
          <span className="pro-crown">
            <CrownFilled />
          </span>
          <div>
            <div className="pro-status-title">
              {isActive ? p.statusActive : expiredOn ? fill(p.statusExpired, { date: dateFmt.format(expiredOn) }) : p.statusFree}
            </div>
            {isActive && expiresAt && (
              <div className="pro-status-sub">
                {fill(p.statusUntil, { date: dateFmt.format(expiresAt), days: plan?.daysLeft ?? 0 })}
              </div>
            )}
            {pricing && (
              <div className="pro-status-sub">
                {fill(p.priceLine, { price: pricing.priceUsd.toFixed(2), days: pricing.durationDays })}
              </div>
            )}
          </div>
        </div>
      </div>

      {expiredOn && bot && <Alert type="warning" showIcon message={p.expiredNotice} style={{ marginBottom: 16 }} />}

      <div className="pro-grid">
        <div className="pro-card">
          <h2>{p.benefitsTitle}</h2>
          <ul className="pro-benefits">
            {p.benefits.map((benefit) => (
              <li key={benefit}>
                <span className="pro-check">
                  <CheckOutlined />
                </span>
                {benefit}
              </li>
            ))}
          </ul>
        </div>

        <div className="pro-card pro-buy">
          <h2>{isActive ? fill(p.renew, { days: pricing?.durationDays ?? 30 }) : p.upgrade}</h2>
          {renderPayButtons()}
        </div>
      </div>

      {renderCheckout()}

      {/* ------------------------- Bot personnel ------------------------- */}
      <div className="pro-card pro-bot">
        <div className="pro-bot-head">
          <span className="pro-bot-icon">
            <RobotOutlined />
          </span>
          <div>
            <h2>{p.botTitle}</h2>
            <p className="pro-muted">{isActive || bot ? p.botIntro : p.botLocked}</p>
          </div>
        </div>

        {bot && (
          <div className="pro-bot-connected">
            <div className="pro-bot-line">
              <CheckCircleFilled className="pro-ok" />
              <strong>{p.connectedTitle}</strong>
              <a href={bot.link} target="_blank" rel="noopener noreferrer" className="pro-bot-name">
                @{bot.username}
              </a>
              {health && (
                <span className={`pro-pill ${health.reachable && health.webhookOk ? 'pro-pill--ok' : 'pro-pill--ko'}`}>
                  {health.reachable && health.webhookOk ? p.webhookOk : p.webhookKo}
                </span>
              )}
            </div>
            {health && health.pendingUpdates > 0 && (
              <p className="pro-muted">{fill(p.pendingUpdates, { count: health.pendingUpdates })}</p>
            )}
            {health?.lastError && (
              <p className="pro-error-line">
                <ExclamationCircleFilled /> {health.lastError}
              </p>
            )}
            <div className="pro-bot-actions">
              <a className="pro-btn" href={bot.link} target="_blank" rel="noopener noreferrer">
                <LinkOutlined /> {p.openBot}
              </a>
              {isActive && (
                <button type="button" className="pro-btn" onClick={repairBot} disabled={botBusy}>
                  <ApiOutlined /> {p.repair}
                </button>
              )}
              <button type="button" className="pro-btn pro-btn--danger" onClick={() => disconnectBot(bot)}>
                <DisconnectOutlined /> {p.disconnect}
              </button>
            </div>
          </div>
        )}

        {isActive && !bot && (
          <form className="pro-bot-form" onSubmit={connectBot}>
            <ol className="pro-steps">
              <li>{p.botStep1}</li>
              <li>{p.botStep2}</li>
              <li>{p.botStep3}</li>
            </ol>
            <label htmlFor="bot-token">{p.tokenLabel}</label>
            <Input.Password
              id="bot-token"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder={p.tokenPlaceholder}
              autoComplete="off"
              spellCheck={false}
              size="large"
            />
            <p className="pro-warning">{p.botWarning}</p>
            {botError && <Alert type="error" showIcon message={botError} />}
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={connecting}
              disabled={token.trim().length < 20}
              className="pro-connect-btn"
            >
              {connecting ? p.connecting : p.connect}
            </Button>
          </form>
        )}

        {bot && botError && <Alert type="error" showIcon message={botError} />}

        {(isActive || bot) && (
          <div className="pro-bot-info">
            <h4>{p.botInfoTitle}</h4>
            <ul>
              {p.botInfo.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default Pro;
